import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import "./chat-ai.css";

const CDN_URL = "https://static.zhihaoscm.cn/";
const AI_IMAGE_URL = `${CDN_URL}customer-admin/ai-dolls-blue.png`;

interface Position {
    x: number;
    y: number;
}

interface DragState extends Position {
    pointerId: number;
    startX: number;
    startY: number;
    moved: boolean;
}

/** 按 Vue ChatAI 的边界值限制位置，并吸附到左右屏幕边缘。 */
function snapToViewport(position: Position): Position {
    const bodyWidth = document.body.clientWidth || window.innerWidth;
    return {
        x: position.x > bodyWidth / 2 - 30 ? Math.max(0, bodyWidth - 63) : 0,
        y: Math.min(Math.max(position.y, 0), Math.max(0, window.innerHeight - 156))
    };
}

/** 复刻 Vue 菜单内的链小云悬浮入口，包括拖拽、吸边和原页面跳转。 */
export function ChatAI() {
    const [position, setPosition] = useState<Position>(() => ({
        x: Math.max(0, (document.body.clientWidth || window.innerWidth) - 63),
        y: Math.max(0, window.innerHeight - 416)
    }));
    const positionRef = useRef(position);
    const dragRef = useRef<DragState | undefined>(undefined);
    const suppressClickRef = useRef(false);

    useEffect(() => {
        positionRef.current = position;
    }, [position]);

    useEffect(() => {
        /** 窗口缩放后沿用 Vue 的重定位规则，避免入口落到可视区外。 */
        const resetPosition = () => setPosition(current => snapToViewport(current));
        window.addEventListener("resize", resetPosition);
        return () => window.removeEventListener("resize", resetPosition);
    }, []);

    const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
            ...positionRef.current,
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            moved: false
        };
    };

    const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        const x = drag.x + event.clientX - drag.startX;
        const y = drag.y + event.clientY - drag.startY;
        drag.moved ||= Math.abs(x - drag.x) > 2 || Math.abs(y - drag.y) > 2;
        setPosition({ x, y });
    };

    const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        suppressClickRef.current = drag.moved;
        dragRef.current = undefined;
        setPosition(current => snapToViewport(current));
    };

    const openChatAI = () => {
        // Vue 会阻止拖拽结束后的 click，避免用户移动入口时误跳页面。
        if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
        }
        const origin = window.location.protocol === "http:" ? "http://localhost:5173" : window.location.origin;
        window.open(`${origin}/chatAI`, "_self");
    };

    return (
        <div
            aria-label="打开链小云"
            className="console-chat-ai"
            onClick={openChatAI}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            role="button"
            style={{ left: position.x, top: position.y }}
            tabIndex={0}
        >
            <img alt="" src={AI_IMAGE_URL} />
        </div>
    );
}
